import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { setSuccessfullModifyDialog } from '../../redux/successfullModifyDialogSlice';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import getCroppedImg, { resolveCroppedOutputMimeType } from '../../utils/getCroppedImg';
import { bakeOrientedImageSrc } from '../../utils/imageOrientation';
import { activeDomainImage, activeLegacyCroppedDocument, entityHasLegacyDocumentPhoto } from '../../utils/imageProxy';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import VideoFileDropzone from '../video/VideoFileDropzone';
import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import Iconify from '../Iconify';
import { entityPhotoHint, validateEntityPhotoFile } from '../../utils/uploadLimits';
import {
  fetchEntityImageSourceAsFile,
  fetchLegacyDocumentSourceAsFile,
  resolveEntityImageEditorSource,
  uploadEntityImage,
} from './entityImageApi';
import { getEntityImageConfig } from './entityImageConfig';
import { syncListEntityImage } from './entityImageListSync';
import { useImagePaste } from './useImagePaste';
import {
  CROP_BACKGROUND_OPTIONS,
  isCropAspectRatioLocked,
  isCropBackgroundHex,
  isTransparentCropBackground,
  normalizeCropBackgroundHex,
  packCroppedAreaPixels,
  pickColorWithEyeDropper,
  resolveCropBackgroundColor,
  resolveCropBackgroundColorInputValue,
  resolveCropperAspect,
  sampleHexColorFromImageElement,
  supportsEyeDropper,
  unpackCroppedAreaPixels,
} from './entityImageCropBackground';
import { resolveFullOutputSize } from './entityImageCropLayout';
import {
  CROP_VIEW_MAX_ZOOM,
  CROP_VIEW_MIN_ZOOM,
} from './entityImageCropView';
import { useCropViewport } from './useCropViewport';
import { normalizeRotationDegrees } from './entityImageCanvasPivot';
import {
  ROTATION_SLIDER_MAX,
  ROTATION_SLIDER_MIN,
  ZOOM_SLIDER_LOG_MAX,
  ZOOM_SLIDER_LOG_MIN,
  rotationToSlider,
  sliderToZoomFactor,
  zoomFactorToSlider,
} from './entityImageSliderScale';
import { stepBoundedValue } from './entityImageSliderStep';
import '../../css/machines/modifierPhoto.css';

/** Crop center offset expressed in half-widths of the photo (layout independent). */
const CROP_PAN_LIMIT = 1.5;

const CHECKERBOARD_PATTERN = `
  linear-gradient(45deg, #d9d9d9 25%, transparent 25%),
  linear-gradient(-45deg, #d9d9d9 25%, transparent 25%),
  linear-gradient(45deg, transparent 75%, #d9d9d9 75%),
  linear-gradient(-45deg, transparent 75%, #d9d9d9 75%)
`;

/** Background picker: swatch only, name in the tooltip — keeps the row on one line. */
const BG_SWATCH_BUTTON_SX = {
  width: 30,
  height: 30,
  minWidth: 0,
  p: 0,
  border: '1px solid',
  borderColor: 'divider',
};

const BG_SWATCH_DOT_SX = {
  width: 16,
  height: 16,
  borderRadius: '50%',
  border: '1px solid #bdbdbd',
  backgroundSize: '6px 6px',
  flexShrink: 0,
};

function getCropperContainerSx(backgroundId) {
  const base = {
    position: 'relative',
    width: '100%',
    maxWidth: 480,
    flex: '1 1 auto',
    minHeight: 0,
    height: '100%',
    marginTop: 0,
    marginLeft: 'auto',
    marginRight: 'auto',
    borderRadius: 1,
    overflow: 'hidden',
  };

  if (isTransparentCropBackground(backgroundId)) {
    return {
      ...base,
      backgroundColor: '#f5f5f5',
      backgroundImage: CHECKERBOARD_PATTERN,
      backgroundSize: '16px 16px',
      backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
    };
  }

  return {
    ...base,
    backgroundColor: resolveCropBackgroundColor(backgroundId),
    backgroundImage: 'none',
  };
}

const COMPACT_SLIDER_SX = {
  py: 0.25,
  px: 0,
  flex: 1,
  minWidth: 0,
  '& .MuiSlider-thumb': { width: 14, height: 14 },
  '& .MuiSlider-rail': { height: 3, left: 0, right: 0, width: 'auto' },
  '& .MuiSlider-track': { height: 3 },
};

const STEPPER_BUTTON_SX = {
  minWidth: 26,
  width: 26,
  height: 26,
  p: 0,
  fontSize: '0.95rem',
  lineHeight: 1,
  flexShrink: 0,
};

const QUARTER_TURN_BUTTON_SX = {
  minWidth: 0,
  height: 26,
  px: 0.6,
  py: 0,
  fontSize: '0.65rem',
  lineHeight: 1,
  flexShrink: 0,
};

function formatPanPercent(pan) {
  const percent = Math.round(Number(pan) * 100);
  if (!Number.isFinite(percent)) {
    return '0%';
  }
  return percent > 0 ? `+${percent}%` : `${percent}%`;
}

function formatZoomLabel(zoomFactor) {
  const zoom = Number(zoomFactor);
  if (!Number.isFinite(zoom)) {
    return 'x1';
  }
  const rounded = Math.round(zoom * 10) / 10;
  return Number.isInteger(rounded) ? `x${rounded}` : `x${rounded.toFixed(1)}`;
}

function formatRotationLabel(degrees) {
  const rounded = Math.round(Number(degrees));
  return `${Number.isFinite(rounded) ? rounded : 0}°`;
}

function CompactSlider({ label, before, after, disabled, ...sliderProps }) {
  const { value, min, max, step = 1, onChange } = sliderProps;
  const numericValue = Number(value);
  const minusValue = stepBoundedValue(numericValue, step, -1, min, max);
  const plusValue = stepBoundedValue(numericValue, step, 1, min, max);
  const hasSideActions = Boolean(before || after);

  const nudge = (next) => {
    if (disabled || next === numericValue || typeof onChange !== 'function') {
      return;
    }
    onChange(null, next);
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: hasSideActions ? 0.75 : 1.5 }}>
        {before}
        <Button
          type="button"
          size="small"
          variant="outlined"
          disabled={disabled || minusValue === numericValue}
          aria-label={`${label} −`}
          onClick={() => nudge(minusValue)}
          sx={STEPPER_BUTTON_SX}
        >
          −
        </Button>
        <Slider size="small" sx={COMPACT_SLIDER_SX} disabled={disabled} {...sliderProps} />
        <Button
          type="button"
          size="small"
          variant="outlined"
          disabled={disabled || plusValue === numericValue}
          aria-label={`${label} +`}
          onClick={() => nudge(plusValue)}
          sx={STEPPER_BUTTON_SX}
        >
          +
        </Button>
        {after}
      </Box>
    </Box>
  );
}

/** Default tall enough for shape / aspect / background / sliders without an inner scrollbar. */
const CONTROLS_HEIGHT_DEFAULT = 320;
const CONTROLS_HEIGHT_MIN = 72;
const CONTROLS_HEIGHT_MAX = 420;
const CONTROLS_COLLAPSE_THRESHOLD = 56;
const CONTROLS_HANDLE_HEIGHT = 28;
/** Keep a usable crop canvas when the tools panel is dragged open. */
const CANVAS_MIN_HEIGHT = 200;
const DIALOG_VIEWPORT_HEIGHT = {
  xs: 'calc(100vh - 24px)',
  sm: 'calc(100dvh - 32px)',
};

/**
 * Unified image editor.
 *
 * - mode="edit" (default): load/upload via `*-image` for an existing entity.
 * - mode="local": prepare a crop in-memory (create flows); calls onLocalConfirm
 *   with { originalFile, croppedFile, croppedArea, aspectRatio, cropShape, rotation }.
 */
export const EntityImageEditor = ({
  entityType,
  entityId,
  entityData,
  modifyPhoto,
  setModifyPhoto,
  mode = 'edit',
  initialFile = null,
  initialImageSrc = null,
  onLocalConfirm = null,
  dialogTitle = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isLocalMode = mode === 'local';
  const config = useMemo(() => getEntityImageConfig(entityType), [entityType]);

  const [errMsg, setErrMsg] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [cropShape, setCropShape] = useState('rect');
  const [isCropping, setIsCropping] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [cropBackground, setCropBackground] = useState('white');
  const [isSamplingCanvasColor, setIsSamplingCanvasColor] = useState(false);
  const eyeDropperAvailable = useMemo(() => supportsEyeDropper(), []);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSource, setIsLoadingSource] = useState(false);
  const [controlsCollapsed, setControlsCollapsed] = useState(false);
  const [controlsHeight, setControlsHeight] = useState(CONTROLS_HEIGHT_DEFAULT);
  const [controlsHeightCap, setControlsHeightCap] = useState(CONTROLS_HEIGHT_MAX);
  const controlsDragRef = useRef(null);
  const editorSplitRef = useRef(null);

  const activeImage = useMemo(
    () => activeDomainImage(entityData, config.imageField),
    [entityData, config.imageField],
  );

  const showEditorChrome =
    Boolean(isLoadingSource) || Boolean(imageSrc && (isCropping || isSaving));
  /** Always offer replace/upload except while saving — not only when there is no DB image. */
  const showUploadZone = !isSaving;
  const showCropper = Boolean(imageSrc && (isCropping || isSaving));
  const cropperContainerSx = useMemo(() => getCropperContainerSx(cropBackground), [cropBackground]);
  const cropperAspect = useMemo(
    () => resolveCropperAspect(cropShape, Number(aspectRatio) || 1),
    [cropShape, aspectRatio],
  );
  const aspectRatioLocked = isCropAspectRatioLocked(cropShape);

  /** Crop geometry: the area is the model, the on-screen view is derived from it. */
  const viewport = useCropViewport({ aspect: cropperAspect, active: showCropper });
  const { crop, zoom, rotation, cropArea, cropFrameSize } = viewport;
  const { reset: resetViewport, restore: restoreViewport } = viewport;
  const cropperCanvasRef = viewport.canvasRef;

  const blobUrlRef = useRef(null);

  const handleCropShapeChange = useCallback(
    (nextShape) => {
      viewport.prepareAspectChange(resolveCropperAspect(nextShape, Number(aspectRatio) || 1));
      setCropShape(nextShape);
    },
    [aspectRatio, viewport],
  );

  const handleAspectRatioChange = useCallback(
    (nextRatio) => {
      viewport.prepareAspectChange(resolveCropperAspect(cropShape, nextRatio));
      setAspectRatio(nextRatio);
    },
    [cropShape, viewport],
  );

  const revokeBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  const setImageSrcFromLoad = useCallback(
    (nextSrc, { trackBlob = false } = {}) => {
      if (trackBlob && nextSrc?.startsWith('blob:')) {
        revokeBlobUrl();
        blobUrlRef.current = nextSrc;
      }
      setImageSrc(nextSrc);
    },
    [revokeBlobUrl],
  );

  const resetEditorState = useCallback(() => {
    revokeBlobUrl();
    setImageSrc(null);
    setOriginalFile(null);
    setIsCropping(false);
    setErrMsg(null);
    resetViewport();
    setCropShape('rect');
    setAspectRatio(1);
    setCropBackground('white');
    setIsSaving(false);
    setIsLoadingSource(false);
    setControlsCollapsed(false);
    setControlsHeight(CONTROLS_HEIGHT_DEFAULT);
    setControlsHeightCap(CONTROLS_HEIGHT_MAX);
  }, [resetViewport, revokeBlobUrl]);

  const applyActiveImageMetadata = useCallback(
    (image) => {
      if (!image) return;

      const { pixels, backgroundColor } = unpackCroppedAreaPixels(image.croppedAreaPixels);
      setCropBackground(backgroundColor);

      if (image.aspectRatio) {
        const ratio = image.aspectRatio === 'free' ? 1 : Number(image.aspectRatio);
        setAspectRatio(Number.isFinite(ratio) ? ratio : 1);
      }

      setCropShape(image.cropShape === 'round' ? 'round' : 'rect');
      const savedRotation = Number(image.rotation);
      restoreViewport({
        area: pixels,
        rotation: Number.isFinite(savedRotation) ? normalizeRotationDegrees(savedRotation) : 0,
      });
    },
    [restoreViewport],
  );

  useEffect(() => {
    if (!modifyPhoto) {
      resetEditorState();
      return undefined;
    }

    if (isLocalMode) {
      if (!imageSrc && (initialImageSrc || initialFile)) {
        let cancelled = false;
        setErrMsg(null);
        resetViewport({ keepCanvas: true });
        setCropShape('rect');
        setAspectRatio(1);
        setCropBackground('white');
        setIsCropping(true);
        if (initialFile) {
          setOriginalFile(initialFile);
        }
        (async () => {
          try {
            const baked = await bakeOrientedImageSrc(initialFile || initialImageSrc, {
              revokeInputBlobUrl: Boolean(initialImageSrc?.startsWith?.('blob:') && initialFile),
            });
            if (cancelled) {
              baked.revoke();
              return;
            }
            setImageSrcFromLoad(baked.src, { trackBlob: baked.src.startsWith('blob:') });
          } catch (error) {
            console.error('Error baking local image orientation:', error);
            if (!cancelled) {
              setErrMsg(t('Impossible de charger l’image source.'));
              setIsCropping(false);
            }
          }
        })();
        return () => {
          cancelled = true;
        };
      }
      return undefined;
    }

    const loadExisting = async () => {
      if (imageSrc) return;

      if (activeImage) {
        setIsCropping(true);
        setIsLoadingSource(true);
        applyActiveImageMetadata(activeImage);

        try {
          const resolved = await resolveEntityImageEditorSource(activeImage);
          if (resolved?.imageSrc) {
            const baked = await bakeOrientedImageSrc(resolved.imageSrc, {
              revokeInputBlobUrl: true,
            });
            setImageSrcFromLoad(baked.src, { trackBlob: true });
            setOriginalFile(resolved.originalFile);
            return;
          }

          const fromApi = await fetchEntityImageSourceAsFile(
            config.route,
            entityId,
            entityType,
            entityId,
          );
          if (fromApi?.imageSrc) {
            const baked = await bakeOrientedImageSrc(fromApi.imageSrc, {
              revokeInputBlobUrl: true,
            });
            setImageSrcFromLoad(baked.src, { trackBlob: true });
            setOriginalFile(fromApi.originalFile);
            return;
          }

          setErrMsg(t('Impossible de charger l’image source.'));
          setIsCropping(false);
        } catch (error) {
          console.error('Error loading source image:', error);
          setErrMsg(t('Impossible de charger l’image source.'));
          setIsCropping(false);
        } finally {
          setIsLoadingSource(false);
        }
        return;
      }

      // Phase 3.2 coexistence: dual-crop `*_document` without `*_image`
      if (
        !config.documentRoute ||
        !entityHasLegacyDocumentPhoto(entityData, {
          imageField: config.imageField,
          documentField: config.documentField,
        })
      ) {
        return;
      }

      setIsCropping(true);
      setIsLoadingSource(true);
      applyActiveImageMetadata(activeLegacyCroppedDocument(entityData?.[config.documentField]));

      try {
        const legacy = await fetchLegacyDocumentSourceAsFile(
          config.documentRoute,
          entityId,
          entityType,
        );
        if (legacy?.imageSrc) {
          const baked = await bakeOrientedImageSrc(legacy.imageSrc, {
            revokeInputBlobUrl: true,
          });
          setImageSrcFromLoad(baked.src, { trackBlob: true });
          setOriginalFile(legacy.originalFile);
          return;
        }
        setErrMsg(t('Impossible de charger l’image source.'));
        setIsCropping(false);
      } catch (error) {
        console.error('Error loading legacy document source:', error);
        setErrMsg(t('Impossible de charger l’image source.'));
        setIsCropping(false);
      } finally {
        setIsLoadingSource(false);
      }
    };

    loadExisting();
  }, [
    modifyPhoto,
    isLocalMode,
    initialFile,
    initialImageSrc,
    activeImage,
    imageSrc,
    config.route,
    config.documentRoute,
    config.imageField,
    config.documentField,
    entityData,
    entityId,
    entityType,
    resetEditorState,
    resetViewport,
    applyActiveImageMetadata,
    setImageSrcFromLoad,
    t,
  ]);

  const handleFileChange = useCallback(
    async (files) => {
      setErrMsg(null);
      if (!files?.length) return;
      if (files.length > 1) {
        setErrMsg(t('Une seule image est autorisée.'));
        return;
      }
      const file = files[0];
      const validation = validateEntityPhotoFile(file, t);
      if (!validation.ok) {
        setErrMsg(validation.message);
        return;
      }
      try {
        const baked = await bakeOrientedImageSrc(file);
        setImageSrcFromLoad(baked.src, { trackBlob: true });
        setOriginalFile(file);
        setIsCropping(true);
        resetViewport({ keepCanvas: true });
      } catch (error) {
        console.error('Error baking upload orientation:', error);
        setErrMsg(t('Impossible de charger l’image source.'));
      }
    },
    [resetViewport, setImageSrcFromLoad, t],
  );

  useImagePaste(modifyPhoto, handleFileChange);

  const handlePickCustomBackgroundColor = useCallback((hex) => {
    const normalized = normalizeCropBackgroundHex(hex);
    if (!normalized) {
      return;
    }
    setCropBackground(normalized);
    setIsSamplingCanvasColor(false);
  }, []);

  const handleEyeDropperBackground = useCallback(async () => {
    if (isSaving || isLoadingSource) {
      return;
    }
    setIsSamplingCanvasColor(false);
    const result = await pickColorWithEyeDropper();
    if (result.ok) {
      handlePickCustomBackgroundColor(result.color);
      return;
    }
    if (result.reason === 'aborted') {
      return;
    }
    if (result.reason === 'unsupported') {
      setIsSamplingCanvasColor(true);
      setErrMsg(t('Cliquez sur la photo pour prélever une couleur.'));
      return;
    }
    setErrMsg(t('Impossible de prélever la couleur. Réessayez.'));
  }, [handlePickCustomBackgroundColor, isLoadingSource, isSaving, t]);

  const handleToggleCanvasColorSample = useCallback(() => {
    if (isSaving || isLoadingSource || !imageSrc) {
      return;
    }
    setIsSamplingCanvasColor((prev) => {
      const next = !prev;
      if (next) {
        setErrMsg(t('Cliquez sur la photo pour prélever une couleur.'));
      } else {
        setErrMsg('');
      }
      return next;
    });
  }, [imageSrc, isLoadingSource, isSaving, t]);

  const handleCanvasColorSampleClick = useCallback(
    (event) => {
      if (!isSamplingCanvasColor) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const img =
        cropperCanvasRef.current?.querySelector('img.reactEasyCrop_Image') ||
        cropperCanvasRef.current?.querySelector('img');
      const hex = sampleHexColorFromImageElement(img, event.clientX, event.clientY);
      if (!hex) {
        setErrMsg(t('Impossible de prélever cette couleur. Cliquez sur la photo.'));
        return;
      }
      handlePickCustomBackgroundColor(hex);
      setErrMsg('');
    },
    [handlePickCustomBackgroundColor, isSamplingCanvasColor, t],
  );

  useEffect(() => {
    setIsSamplingCanvasColor(false);
  }, [imageSrc]);

  // Cap tools-panel height so dragging only moves the split — never grows the dialog.
  useEffect(() => {
    const node = editorSplitRef.current;
    if (!node || !showEditorChrome) {
      return undefined;
    }

    const updateCap = () => {
      const available = Math.max(0, node.clientHeight);
      const nextCap = Math.max(
        CONTROLS_HEIGHT_MIN,
        Math.min(CONTROLS_HEIGHT_MAX, available - CANVAS_MIN_HEIGHT),
      );
      setControlsHeightCap(nextCap);
      setControlsHeight((prev) => Math.min(prev, nextCap));
    };

    updateCap();
    const observer = new ResizeObserver(updateCap);
    observer.observe(node);
    return () => observer.disconnect();
  }, [showEditorChrome, modifyPhoto]);

  const handleUploadPic = async (origFile, croppedFile, areaForSave = cropArea) => {
    setErrMsg(null);
    setIsSaving(true);
    try {
      const response = await uploadEntityImage(config, entityId, {
        originalFile: origFile,
        croppedFile,
        croppedArea: areaForSave,
        aspectRatio: cropperAspect,
        cropShape,
        rotation: normalizeRotationDegrees(rotation),
      });
      if (response.status === 200 || response.status === 201) {
        setModifyPhoto(false);
        dispatch(setSuccessfullModifyDialog({ open: true, type: t('Modifier l’image') }));
        dispatch(
          config.setDetailsData({
            ...entityData,
            [config.imageField]: [response.data],
            ...(config.documentField ? { [config.documentField]: [] } : {}),
          }),
        );
        syncListEntityImage({
          entityId,
          imageField: config.imageField,
          image: response.data,
          documentField: config.documentField,
        });
      }
    } catch (err) {
      console.error('Error updating entity image:', err);
      CustomErrorLogger(err, `EntityImageEditor.${entityType}`);
      setErrMsg(t('Erreur lors de l’ajout de l’image.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCropConfirm = async () => {
    setErrMsg(null);
    try {
      if (!imageSrc || !cropArea?.width || !cropArea?.height) {
        setErrMsg(t('Erreur lors de la découpe de l’image.'));
        return;
      }
      const backgroundColor = isTransparentCropBackground(cropBackground)
        ? 'transparent'
        : resolveCropBackgroundColor(cropBackground);
      const outputMime = resolveCroppedOutputMimeType({
        mimeType: originalFile?.type,
        cropShape,
        backgroundColor: isTransparentCropBackground(cropBackground) ? 'transparent' : null,
      });
      // The area already carries the frame aspect exactly (see areaFromView).
      const fullSize = resolveFullOutputSize(cropArea.width / cropArea.height);
      const areaForSave = packCroppedAreaPixels(cropArea, cropBackground);
      const croppedBlob = await getCroppedImg(imageSrc, cropArea, {
        rotation: normalizeRotationDegrees(rotation),
        cropShape,
        mimeType: originalFile?.type,
        backgroundColor,
        outputWidth: fullSize.width,
        outputHeight: fullSize.height,
      });
      const ext = outputMime === 'image/png' ? 'png' : 'jpg';
      const baseName = originalFile?.name?.replace(/\.[^.]+$/, '') ?? 'cropped';
      const croppedFile = new File([croppedBlob], `${baseName}-crop.${ext}`, {
        type: croppedBlob.type,
      });

      if (isLocalMode) {
        if (!onLocalConfirm) {
          setErrMsg(t('Erreur lors de la découpe de l’image.'));
          return;
        }
        setIsSaving(true);
        try {
          await onLocalConfirm({
            originalFile,
            croppedFile,
            croppedArea: areaForSave,
            aspectRatio: cropperAspect,
            cropShape,
            rotation: normalizeRotationDegrees(rotation),
          });
          setModifyPhoto(false);
        } finally {
          setIsSaving(false);
        }
        return;
      }

      await handleUploadPic(originalFile, croppedFile, areaForSave);
    } catch (error) {
      console.error('EntityImageEditor crop failed:', error);
      CustomErrorLogger(error, `EntityImageEditor.crop.${entityType}`);
      setErrMsg(t('Erreur lors de la découpe de l’image.'));
    }
  };

  const onControlsHandlePointerDown = useCallback((event) => {
    event.preventDefault();
    controlsDragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight: controlsCollapsed ? CONTROLS_HANDLE_HEIGHT : controlsHeight,
      heightCap: controlsHeightCap,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [controlsCollapsed, controlsHeight, controlsHeightCap]);

  const onControlsHandlePointerMove = useCallback((event) => {
    const drag = controlsDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const delta = drag.startY - event.clientY;
    if (Math.abs(delta) > 4) {
      drag.moved = true;
    }
    const next = Math.max(0, Math.min(drag.heightCap, drag.startHeight + delta));
    if (next < CONTROLS_COLLAPSE_THRESHOLD) {
      setControlsCollapsed(true);
    } else {
      setControlsCollapsed(false);
      setControlsHeight(Math.max(CONTROLS_HEIGHT_MIN, next));
    }
  }, []);

  const onControlsHandlePointerUp = useCallback((event) => {
    const drag = controlsDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    controlsDragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore
    }
    if (!drag.moved) {
      setControlsCollapsed((prev) => !prev);
    }
  }, []);

  const controlsPanelHeight = controlsCollapsed ? CONTROLS_HANDLE_HEIGHT : controlsHeight;

  return (
    <Dialog
      open={modifyPhoto}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          // When the crop UI is open, use the full available viewport so tools
          // fit without scrolling; the split handle only redistributes inside.
          height: showEditorChrome ? DIALOG_VIEWPORT_HEIGHT : 'auto',
          maxHeight: DIALOG_VIEWPORT_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          m: { xs: 1, sm: 2 },
          overflow: 'hidden',
        },
      }}
    >
      <Box onClick={() => setModifyPhoto(false)} className="modifierPhoto_icon_text">
        <Typography className="modifierPhoto_title">
          {dialogTitle || (isLocalMode ? t('Préparer l’image') : t('modifier l’image'))}
        </Typography>
        <Iconify icon="material-symbols:close" className="modifierPhoto_close_icon" />
      </Box>
      <DialogContent
        className="modifierPhoto_dialog_content"
        sx={{
          pt: 1,
          pb: 1,
          px: { xs: 1.5, sm: 2 },
          gap: 1,
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <Typography className="modifierPhoto_typography" sx={{ ml: 0, mb: 0 }}>
          {t(config.editorSubtitle)}
        </Typography>
        {errMsg && <Typography className="modifierPhoto_error_message">{errMsg}</Typography>}
        {showUploadZone && (
          <VideoFileDropzone
            accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
            hint={entityPhotoHint(t)}
            disabled={isSaving || isLoadingSource}
            onFiles={handleFileChange}
            onPasteError={(reason) => {
              if (reason === 'empty') {
                setErrMsg(t('Aucune image dans le presse-papiers.'));
                return;
              }
              if (reason === 'denied') {
                setErrMsg(t("Autorisez l'accès au presse-papiers pour coller une image."));
                return;
              }
              if (reason === 'unsupported') {
                setErrMsg(t('Le collage depuis le presse-papiers n’est pas disponible sur cet appareil.'));
                return;
              }
              setErrMsg(t("Impossible de coller l'image. Réessayez ou utilisez Parcourir."));
            }}
          />
        )}

        {showEditorChrome && (
          <Box
            ref={editorSplitRef}
            sx={{
              width: '100%',
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              overflow: 'hidden',
            }}
          >
            <Box
              ref={cropperCanvasRef}
              onClick={handleCanvasColorSampleClick}
              sx={{
                ...cropperContainerSx,
                flex: '1 1 auto',
                minHeight: CANVAS_MIN_HEIGHT,
                maxHeight: '100%',
                cursor: isSamplingCanvasColor ? 'crosshair' : undefined,
              }}
            >
              {isLoadingSource || !imageSrc ? (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={36} />
                </Box>
              ) : (
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  minZoom={CROP_VIEW_MIN_ZOOM}
                  maxZoom={CROP_VIEW_MAX_ZOOM}
                  rotation={rotation}
                  cropShape={cropShape}
                  aspect={cropperAspect}
                  cropSize={cropFrameSize || undefined}
                  objectFit="contain"
                  restrictPosition={false}
                  onCropChange={viewport.onCropChange}
                  onZoomChange={viewport.onZoomChange}
                  onRotationChange={viewport.onRotationChange}
                  onWheelRequest={() => false}
                  onInteractionStart={viewport.onInteractionStart}
                  onInteractionEnd={viewport.onInteractionEnd}
                  onMediaLoaded={viewport.onMediaLoaded}
                  style={{
                    containerStyle: { width: '100%', height: '100%' },
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                height: controlsPanelHeight,
                display: 'flex',
                flexDirection: 'column',
                borderTop: '1px solid #E8E8E8',
                bgcolor: '#FAFAFA',
                borderRadius: '8px 8px 0 0',
                mt: 0.5,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box
                role="separator"
                aria-orientation="horizontal"
                aria-label={t('Options de recadrage')}
                aria-expanded={!controlsCollapsed}
                onPointerDown={onControlsHandlePointerDown}
                onPointerMove={onControlsHandlePointerMove}
                onPointerUp={onControlsHandlePointerUp}
                onPointerCancel={onControlsHandlePointerUp}
                sx={{
                  flexShrink: 0,
                  height: CONTROLS_HANDLE_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'ns-resize',
                  userSelect: 'none',
                  touchAction: 'none',
                  '&:hover .entityImageControlsHandleChevron': {
                    borderTopColor: '#9E9E9E',
                    borderBottomColor: '#9E9E9E',
                  },
                }}
              >
                <Box
                  className="entityImageControlsHandleChevron"
                  aria-hidden
                  sx={{
                    width: 0,
                    height: 0,
                    borderLeft: '10px solid transparent',
                    borderRight: '10px solid transparent',
                    ...(controlsCollapsed
                      ? { borderBottom: '5px solid #C7C7C7' }
                      : { borderTop: '5px solid #C7C7C7' }),
                  }}
                />
                {controlsCollapsed && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ position: 'absolute', right: 12, fontSize: '0.7rem' }}
                  >
                    {t('Options')}
                  </Typography>
                )}
              </Box>

              {!controlsCollapsed && (
                <Box
                  sx={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 1.5,
                    pb: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: aspectRatioLocked ? 'auto' : 'auto 1fr',
                      },
                      gap: 1.5,
                      alignItems: 'end',
                      width: '100%',
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {t('Forme')}
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={cropShape}
                        disabled={isSaving || isLoadingSource}
                        onChange={(_event, value) => {
                          if (value) handleCropShapeChange(value);
                        }}
                      >
                        <ToggleButton value="rect">{t('Rectangle')}</ToggleButton>
                        <ToggleButton value="round">{t('Rond')}</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    {!aspectRatioLocked && (
                      <FormControl size="small" sx={{ minWidth: 0 }} disabled={isSaving || isLoadingSource}>
                        <InputLabel>{t("Format d'image")}</InputLabel>
                        <Select
                          value={aspectRatio}
                          onChange={(event) => {
                            const next = Number(event.target.value);
                            if (!Number.isFinite(next) || next <= 0) {
                              return;
                            }
                            handleAspectRatioChange(next);
                          }}
                          label={t("Format d'image")}
                        >
                          <MenuItem value={1}>{t('Carré (1:1)')}</MenuItem>
                          <MenuItem value={16 / 9}>{t('Rectangle horizontal (16:9)')}</MenuItem>
                          <MenuItem value={4 / 3}>{t('Rectangle horizontal (4:3)')}</MenuItem>
                          <MenuItem value={3 / 4}>{t('Rectangle vertical (3:4)')}</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Box>

                  <Box sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('Couleur de fond')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {CROP_BACKGROUND_OPTIONS.map((option) => (
                        <Tooltip key={option.id} title={t(option.labelKey)}>
                          <Box component="span" sx={{ display: 'inline-flex' }}>
                            <ToggleButton
                              size="small"
                              value={option.id}
                              selected={cropBackground === option.id}
                              disabled={isSaving || isLoadingSource}
                              aria-label={t(option.labelKey)}
                              onClick={() => {
                                setCropBackground(option.id);
                                setIsSamplingCanvasColor(false);
                              }}
                              sx={BG_SWATCH_BUTTON_SX}
                            >
                              <Box
                                sx={{
                                  ...BG_SWATCH_DOT_SX,
                                  backgroundColor: option.color ?? 'transparent',
                                  backgroundImage: option.color ? 'none' : CHECKERBOARD_PATTERN,
                                }}
                              />
                            </ToggleButton>
                          </Box>
                        </Tooltip>
                      ))}

                      <Tooltip title={t('Couleur personnalisée')}>
                        <ToggleButton
                          size="small"
                          value="custom"
                          component="label"
                          selected={isCropBackgroundHex(cropBackground)}
                          disabled={isSaving || isLoadingSource}
                          aria-label={t('Couleur personnalisée')}
                          data-testid="entityImageBgColorPicker"
                          sx={{ ...BG_SWATCH_BUTTON_SX, width: 42, gap: 0.25, cursor: 'pointer' }}
                        >
                          <Box
                            sx={{
                              ...BG_SWATCH_DOT_SX,
                              backgroundColor: resolveCropBackgroundColorInputValue(cropBackground),
                            }}
                          />
                          <Iconify
                            icon="material-symbols:palette-outline"
                            sx={{ width: 13, height: 13, color: 'text.secondary' }}
                          />
                          <Box
                            component="input"
                            type="color"
                            disabled={isSaving || isLoadingSource}
                            value={resolveCropBackgroundColorInputValue(cropBackground)}
                            onChange={(event) => handlePickCustomBackgroundColor(event.target.value)}
                            onClick={(event) => event.stopPropagation()}
                            data-testid="entityImageBgColorInput"
                            sx={{
                              position: 'absolute',
                              width: 1,
                              height: 1,
                              padding: 0,
                              margin: -1,
                              overflow: 'hidden',
                              clip: 'rect(0, 0, 0, 0)',
                              whiteSpace: 'nowrap',
                              border: 0,
                            }}
                          />
                        </ToggleButton>
                      </Tooltip>

                      {eyeDropperAvailable ? (
                        <Tooltip title={t('Pipette')}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={isSaving || isLoadingSource}
                              onClick={handleEyeDropperBackground}
                              aria-label={t('Pipette')}
                              data-testid="entityImageBgEyeDropper"
                              sx={{ ...BG_SWATCH_BUTTON_SX, borderColor: 'primary.main' }}
                            >
                              <Iconify
                                icon="material-symbols:colorize-outline"
                                sx={{ width: 17, height: 17 }}
                              />
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title={t('Pipette (photo)')}>
                          <span>
                            <Button
                              size="small"
                              variant={isSamplingCanvasColor ? 'contained' : 'outlined'}
                              disabled={isSaving || isLoadingSource || !imageSrc}
                              onClick={handleToggleCanvasColorSample}
                              aria-label={t('Pipette (photo)')}
                              data-testid="entityImageBgCanvasSample"
                              sx={{ ...BG_SWATCH_BUTTON_SX, borderColor: 'primary.main' }}
                            >
                              <Iconify
                                icon="material-symbols:colorize-outline"
                                sx={{ width: 17, height: 17 }}
                              />
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: { xs: 0.5, sm: 1.5 },
                      width: '100%',
                      opacity: showCropper ? 1 : 0.5,
                      pointerEvents: showCropper ? 'auto' : 'none',
                    }}
                  >
                    <CompactSlider
                      label={`${t('Position X')} (${formatPanPercent(viewport.pan.x)})`}
                      value={viewport.pan.x}
                      min={-CROP_PAN_LIMIT}
                      max={CROP_PAN_LIMIT}
                      step={0.01}
                      disabled={isSaving || isLoadingSource}
                      onChange={(_event, value) => viewport.setPan('x', value)}
                      aria-label={t('Position X')}
                    />
                    <CompactSlider
                      label={`${t('Position Y')} (${formatPanPercent(viewport.pan.y)})`}
                      value={viewport.pan.y}
                      min={-CROP_PAN_LIMIT}
                      max={CROP_PAN_LIMIT}
                      step={0.01}
                      disabled={isSaving || isLoadingSource}
                      onChange={(_event, value) => viewport.setPan('y', value)}
                      aria-label={t('Position Y')}
                    />
                    <CompactSlider
                      label={`${t('Zoom')} (${formatZoomLabel(viewport.zoomFactor)})`}
                      value={zoomFactorToSlider(viewport.zoomFactor)}
                      min={ZOOM_SLIDER_LOG_MIN}
                      max={ZOOM_SLIDER_LOG_MAX}
                      step={0.01}
                      disabled={isSaving || isLoadingSource}
                      onChange={(_event, value) => viewport.setCoverage(1 / sliderToZoomFactor(value))}
                      aria-label={t('Zoom')}
                    />
                    <CompactSlider
                      label={`${t('Rotation')} (${formatRotationLabel(rotationToSlider(rotation))})`}
                      before={(
                        <Button
                          type="button"
                          size="small"
                          variant="outlined"
                          disabled={isSaving || isLoadingSource}
                          sx={QUARTER_TURN_BUTTON_SX}
                          onClick={() => viewport.rotateBy(-90)}
                        >
                          −90°
                        </Button>
                      )}
                      after={(
                        <Button
                          type="button"
                          size="small"
                          variant="outlined"
                          disabled={isSaving || isLoadingSource}
                          sx={QUARTER_TURN_BUTTON_SX}
                          onClick={() => viewport.rotateBy(90)}
                        >
                          +90°
                        </Button>
                      )}
                      value={rotationToSlider(rotation)}
                      min={ROTATION_SLIDER_MIN}
                      max={ROTATION_SLIDER_MAX}
                      step={1}
                      disabled={isSaving || isLoadingSource}
                      onChange={(_event, value) => viewport.rotateTo(value)}
                      aria-label={t('Rotation')}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions className="modifierPhoto_dialog_actions">
        <Button className="modifierPhoto_button_annuler" disabled={isSaving} onClick={() => setModifyPhoto(false)}>
          {t('Annuler')}
        </Button>
        {showEditorChrome && (
          <Button
            className="updateUserName_button_valider"
            variant="contained"
            disabled={isSaving || isLoadingSource || !imageSrc}
            onClick={handleCropConfirm}
          >
            {isSaving
              ? isLocalMode
                ? t('Préparation…')
                : t('Enregistrement…')
              : t('Valider')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
